import React, { useEffect, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useCarrito } from '../../context/CarritoContext';
import { generarTituloProducto, toNumber, formatPrecio, obtenerMedidasProducto } from '../../utils/formatters';
import {
  obtenerURLImagen,
  obtenerImagenesProducto,
  obtenerCoverImagen,
} from '../../utils/imageUtils';
import { labelCajaTipo } from '../../constants';
import OptimizedImage from '../common/OptimizedImage';
import './CardProducto.css';

function ProductDetailModal({ producto, show, onHide }) {
  const [cantidad, setCantidad] = useState(1);
  const [galleryIndex, setGalleryIndex] = useState(0);
  const { agregarAlCarrito } = useCarrito();
  const scrollYRef = useRef(0);

  const titulo = producto ? generarTituloProducto(producto) : '';
  const esCaja = producto?.categoria === 'fundas' || producto?.categoria == null;
  const coverSrc = producto ? obtenerCoverImagen(producto) : '';
  const imagenes = producto ? obtenerImagenesProducto(producto) : [];
  const gallerySrc = obtenerURLImagen(imagenes[galleryIndex] || coverSrc);
  const medidas = producto && esCaja ? obtenerMedidasProducto(producto) : null;
  const packHint =
    producto && !esCaja && producto.stock != null && producto.stock > 0
      ? `Stock: ${producto.stock} u.`
      : null;

  const restoreScroll = () => {
    const y = scrollYRef.current;
    const html = document.documentElement;
    const prev = html.style.scrollBehavior;
    html.style.scrollBehavior = 'auto';
    window.scrollTo(0, y);
    requestAnimationFrame(() => {
      window.scrollTo(0, y);
      html.style.scrollBehavior = prev;
    });
  };

  useEffect(() => {
    if (show) {
      setGalleryIndex(0);
      setCantidad(1);
      scrollYRef.current = window.scrollY;
    }
  }, [show, producto?.id]);

  useEffect(() => {
    if (!show) return undefined;
    restoreScroll();
    const t = window.setTimeout(restoreScroll, 80);
    return () => window.clearTimeout(t);
  }, [show]);

  if (!producto) return null;

  const caracteristicas = [
    (medidas
      ? {
          label: 'Medida',
          value: `${medidas.ancho} x ${medidas.alto} x ${medidas.profundidad} cm`,
        }
      : producto.modelo && { label: 'Medida', value: producto.modelo }),
    producto.material && {
      label: esCaja ? 'Categoría' : 'Tipo',
      value: esCaja ? labelCajaTipo(producto.material) : producto.material,
    },
    !esCaja && producto.color && { label: 'Color', value: producto.color },
  ].filter(Boolean);

  const handleAgregar = () => {
    agregarAlCarrito(
      {
        id: producto.id,
        nombre: titulo,
        precio: toNumber(producto.precio),
        imagenURL: coverSrc,
      },
      cantidad
    );
    onHide();
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      onShow={restoreScroll}
      onEntered={restoreScroll}
      centered
      size="lg"
      autoFocus
      restoreFocus={false}
      className="product-detail-modal-root"
      backdropClassName="product-detail-backdrop"
      dialogClassName="product-detail-dialog"
      contentClassName="product-detail-content"
    >
      <Modal.Body className="product-detail-body">
        <button
          type="button"
          className="product-detail-close"
          aria-label="Cerrar"
          onClick={onHide}
        >
          ×
        </button>
        <div className="product-detail-grid">
          <div className="product-detail-gallery">
            <OptimizedImage
              key={`gallery-${producto.id}-${galleryIndex}`}
              src={gallerySrc}
              alt={`${titulo} — imagen ${galleryIndex + 1}`}
              className="product-detail-img"
              eager
            />
            {imagenes.length > 1 && (
              <div className="product-detail-thumbs" role="tablist" aria-label="Galería de imágenes">
                {imagenes.map((src, idx) => (
                  <button
                    key={`${src}-${idx}`}
                    type="button"
                    role="tab"
                    aria-selected={idx === galleryIndex}
                    className={`product-detail-thumb${idx === galleryIndex ? ' is-active' : ''}`}
                    onClick={() => setGalleryIndex(idx)}
                  >
                    <img src={obtenerURLImagen(src)} alt="" />
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="product-detail-info">
            <h3 className="product-detail-title">{titulo}</h3>
            <p className="product-detail-price">{formatPrecio(producto.precio)}</p>
            <p className="product-detail-iva">IVA incluido</p>
            {producto.descripcion && (
              <p className="product-detail-desc">{producto.descripcion}</p>
            )}
            {caracteristicas.length > 0 && (
              <ul className="product-detail-specs">
                {caracteristicas.map((c) => (
                  <li key={c.label}>
                    <strong>{c.label}:</strong> {c.value}
                  </li>
                ))}
              </ul>
            )}
            {packHint && <p className="product-detail-stock">{packHint}</p>}
            <div className="product-detail-qty">
              <label htmlFor={`qty-detail-${producto.id}`}>Cantidad</label>
              <input
                id={`qty-detail-${producto.id}`}
                type="number"
                min={1}
                max={999}
                value={cantidad}
                onChange={(e) => setCantidad(Math.max(1, Number(e.target.value) || 1))}
              />
            </div>
            <button
              type="button"
              className="product-btn product-detail-add"
              onClick={handleAgregar}
            >
              Agregar al carrito
            </button>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
}

export default ProductDetailModal;
